import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Flex,
  Text
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { FaClock, FaEdit, FaEye, FaFile, FaTrashAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import SearchTable2 from "components/Tables/SearchTable2";
import Loading from "components/Loading/Loading";
import { useCustomToast } from "components/CustomToast/CustomToast";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import { userService } from "services/userService";
import { ROUTES } from "constants/routes-path";
import { getEmptyPageResponse, toTablePageableData } from "utils/utils";

const UserList = () => {
  const { t } = useTranslation();
  const [response, setResponse] = useState(undefined);
  const [searchParams, setSearchParams] = useState({});
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const dialogRef = useRef(undefined);
  const [userToDelete, setUserToDelete] = useState(null);

  const columns = useMemo(() => [
    { Header: t("settings.user.list.table.header.login"), accessor: "login" },
    { Header: t("settings.user.list.table.header.registration"), accessor: "matricula" },
    { Header: t("settings.user.list.table.header.name"), accessor: "nome_pessoa", id: "pessoa.nome:person.nome" },
    {
      Header: t("settings.user.list.table.header.active"),
      accessor: "ativo",
      custom: ({ value }) => (value ? t("system.labels.true") : t("system.labels.false"))
    },
    { Header: t("settings.user.list.table.header.function"), accessor: "funcao" },
    {
      Header: t("settings.user.list.table.header.profiles"),
      accessor: "perfis",
      disableSortBy: true,
      custom: ({ value }) => (value.map?.((e, key) => <Text key={key}>{`- ${e}`}</Text>)),
    },
    { Header: t("settings.user.list.table.header.creation"), accessor: "data_criacao" },
    {
      Header: t("settings.user.list.table.header.operations"),
      disableSortBy: true,
      actions: [
        {
          label: t("system.actions.details"),
          permission: "visualizar-usuario",
          onClick: (row) => navigate(ROUTES.usersDetailsPath.replace(":id", row.original.id)),
          icon: <FaEye />
        },
        {
          label: t("system.actions.edit"),
          permission: "editar-usuario",
          onClick: (row) => navigate(ROUTES.usersEditPath.replace(":id", row.original.id)),
          icon: <FaEdit />
        },
        {
          label: t("settings.user.list.actions.documents"),
          permission: "documentos-usuario",
          onClick: (row) =>
            navigate(`${ROUTES.documentsPath}/?parent_id=${row.original.id}&parent_type=usuarios`),
          icon: <FaFile />
        },
        {
          label: t("settings.user.list.actions.access-time"),
          permission: "horarios-acesso-usuario",
          onClick: (row) => navigate(`${ROUTES.usersAccessTimePath}/?parent_id=${row.original.id}`),
          icon: <FaClock />
        },
        {
          label: t("system.actions.remove"),
          permission: "editar-usuario",
          onClick: (row) => handleRemove(row.original.id),
          icon: <FaTrashAlt color="#E53E3E" />,
        },
      ],
    }
  ], []);

  const fetchData = (params = {}) => {
    params.orderBy = params.orderBy ?? "login";
    params.page = Math.min(params?.page ?? 1, response?.total_pages ?? Infinity) || 1;
    setSearchParams(params);
    if (response) setResponse(undefined);

    userService.list(params)
      .then(setResponse)
      .catch(err => {
        setResponse(getEmptyPageResponse(searchParams));
        showToast({ title: t(err), status: "error" });
      });
  };

  const handleConfirmRemove = async () => {
    if (userToDelete) {
      try {
        await userService.delete(userToDelete);
        showToast({ title: t('settings.user.messages.success.delete'), status: "success" });
        fetchData();
      } catch (err) {
        showToast({ title: t(err), status: "error" });
      }
    }
  };

  const handleRemove = (userId) => {
    setUserToDelete(userId);
    dialogRef.current?.displayDialog({
      title: t("settings.user.list.actions.remove-user"),
      message: t("system.actions.remove-confirm"),
    });
  };

  useEffect(() => fetchData(), []);

  return (
    <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
      <Flex w="100%" px="30px" justify="flex-start" align="center" gap="4" direction={{ sm: "column", md: "row" }}>
        <Button
          w="145px"
          variant="list-primary"
          alignSelf={{ sm: "flex-start", lg: undefined }}
          leftIcon={<AddIcon fontSize="sm" />}
          onClick={() => navigate(ROUTES.usersAddPath)}
        >
          {t("settings.user.list.actions.new")}
        </Button>
      </Flex>

      <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
        <ListPageCardHeader title={t("settings.user.title")} subtitle={t("settings.user.description")} />
        <CardBody>
          {response ? (
            <SearchTable2
              columnsData={columns}
              tableData={response.content}
              pageableData={toTablePageableData(response, { defaultField: "login", ...searchParams })}
              onChange={fetchData}
            />
          ) : (
            <Loading maxH={{ sm: "100px", md: "350px" }} />
          )}
        </CardBody>
      </Card>
      <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmRemove} confirmButtonProps={{ colorScheme: "red" }} />
    </Flex>
  );
}

export default UserList;
